/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Connected apps: the third-party apps this person has authorised to act as
 * them, and a way to disconnect each one. Everyone has this page; an admin's
 * view of every authorisation lives in Developer settings.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Avatar, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { ArrowBack, Apps } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const CARD_SX = {
  p: 3,
  border: `1px solid ${colors.callCenter.cardBorder}`,
  boxShadow: "none",
  borderRadius: "12px",
};

const when = (iso) => (iso ? new Date(iso).toLocaleDateString() : "never");

export default function ConnectedApps() {
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const [apps, setApps] = useState(null);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthReady || !isAuthenticated) return;
    try {
      const res = await axiosInstance.get("/oauth/connected-apps");
      setApps(res.data || []);
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not load your connected apps."));
      setApps([]);
    }
  }, [axiosInstance, isAuthReady, isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  const disconnect = async () => {
    setBusy(true);
    try {
      await axiosInstance.delete(`/oauth/connected-apps/${confirm.grant_id}`);
      setConfirm(null);
      load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not disconnect the app."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isV2 ? (
          <PageHeader title="Connected apps" summary="Apps you have allowed to use PearlCRM as you." />
        ) : (
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate("/settings")} aria-label="Back to settings">
            <ArrowBack />
          </IconButton>
          <Apps sx={{ color: colors.brand.primary, fontSize: 32 }} />
          <MDBox ml={0.5}>
            <MDTypography variant="h5" fontWeight="medium">Connected apps</MDTypography>
            <MDTypography variant="body2" color="text">Apps you have allowed to use PearlCRM as you.</MDTypography>
          </MDBox>
        </MDBox>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {apps === null && <MDBox py={6} textAlign="center"><CircularProgress /></MDBox>}

        {apps && apps.length === 0 && (
          <Card sx={{ ...CARD_SX, textAlign: "center", py: 6 }}>
            <MDTypography variant="body2" color="text">You have not connected any apps.</MDTypography>
          </Card>
        )}

        {apps && apps.map((a) => (
          <Card key={a.grant_id} sx={{ ...CARD_SX, mb: 2 }}>
            <MDBox display="flex" alignItems="flex-start" gap={2}>
              <Avatar src={a.client.logo_url || undefined} alt="">{a.client.name.charAt(0)}</Avatar>
              <MDBox flexGrow={1}>
                <MDTypography variant="h6">{a.client.name}</MDTypography>
                {a.client.description && (
                  <MDTypography variant="body2" color="text">{a.client.description}</MDTypography>
                )}
                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  Connected {when(a.authorised_at)} · last used {when(a.last_used_at)}
                </MDTypography>
                <MDTypography variant="caption" display="block" mt={1}>
                  Can: {a.scopes.map((s) => s.description).join(", ")}
                </MDTypography>
              </MDBox>
              <MDButton variant="outlined" color="error" size="small" onClick={() => setConfirm(a)}>
                Disconnect
              </MDButton>
            </MDBox>
          </Card>
        ))}
      </MDBox>

      <Dialog open={Boolean(confirm)} onClose={busy ? undefined : () => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Disconnect {confirm?.client.name}?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">
            It loses access within a minute and has to ask you again to reconnect.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setConfirm(null)} disabled={busy}>Cancel</MDButton>
          <MDButton variant="gradient" color="error" onClick={disconnect} disabled={busy}>
            {busy ? "Disconnecting…" : "Disconnect"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
